import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

interface PendingRequest {
  promise: Promise<AxiosResponse>;
  timestamp: number;
}

interface CachedResponse {
  data: any;
  timestamp: number;
}

const pendingRequests = new Map<string, PendingRequest>();
const responseCache = new Map<string, CachedResponse>();

const CACHE_TTL = 5000; // 5 seconds
const REQUEST_TIMEOUT = 30000; // 30 seconds

/**
 * Generate a unique key for a request based on method, URL, params, and data
 */
function getRequestKey(config: AxiosRequestConfig): string {
  const method = (config.method || 'GET').toUpperCase();
  const url = config.url || '';
  const params = JSON.stringify(config.params || {});
  const data = JSON.stringify(config.data || {});
  return `${method}:${url}:${params}:${data}`;
}

/**
 * Clean up old cache entries and pending requests
 */
function cleanup() {
  const now = Date.now();
  
  // Remove expired cache entries
  for (const [key, cached] of responseCache.entries()) {
    if (now - cached.timestamp > CACHE_TTL) {
      responseCache.delete(key);
    }
  }
  
  // Remove stale pending requests (shouldn't happen, but safety check)
  for (const [key, pending] of pendingRequests.entries()) {
    if (now - pending.timestamp > REQUEST_TIMEOUT) {
      pendingRequests.delete(key);
    }
  }
}

/**
 * Setup request deduplication interceptors
 * This prevents duplicate API calls and caches recent responses
 */
export function setupRequestDeduplication() {
  // Clean up periodically
  setInterval(cleanup, 10000); // Every 10 seconds
  
  // Request interceptor
  axios.interceptors.request.use(
    (config) => {
      // Skip deduplication for certain requests if needed
      if ((config as any).skipDeduplication) {
        return config;
      }
      
      // NEVER cache or deduplicate POST/PUT/DELETE/PATCH requests - they're mutations!
      const method = (config.method || 'GET').toUpperCase();
      if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
        return config;
      }
      
      const key = getRequestKey(config);
      
      // Check cache first
      const cached = responseCache.get(key);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        // Return a rejected promise that will be caught by response interceptor
        return Promise.reject({
          __CACHED: true,
          data: cached.data,
          config,
        } as any);
      }
      
      // Check if request is already pending
      const pending = pendingRequests.get(key);
      if (pending) {
        // Return a rejected promise that will be caught by response interceptor
        return Promise.reject({
          __DEDUPED: true,
          promise: pending.promise,
          config,
        } as any);
      }
      
      // Create new request
      const requestPromise = axios(config)
        .then((response) => {
          // Cache successful responses
          responseCache.set(key, {
            data: response.data,
            timestamp: Date.now(),
          });
          pendingRequests.delete(key);
          return response;
        })
        .catch((error) => {
          pendingRequests.delete(key);
          throw error;
        });
      
      pendingRequests.set(key, {
        promise: requestPromise,
        timestamp: Date.now(),
      });
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  
  // Response interceptor to handle cached/deduped requests
  axios.interceptors.response.use(
    (response) => response,
    (error: any) => {
      // Handle cached response
      if (error.__CACHED) {
        return Promise.resolve({
          data: error.data,
          status: 200,
          statusText: 'OK',
          headers: {},
          config: error.config,
        } as AxiosResponse);
      }
      
      // Handle deduped request
      if (error.__DEDUPED) {
        return error.promise;
      }
      
      return Promise.reject(error);
    }
  );
}

/**
 * Clear cache for a specific request pattern
 * Useful when you know data has changed
 */
export function clearCache(pattern?: string) {
  if (pattern) {
    // Clear entries matching pattern
    for (const key of responseCache.keys()) {
      if (key.includes(pattern)) {
        responseCache.delete(key);
      }
    }
  } else {
    // Clear all cache
    responseCache.clear();
  }
}

/**
 * Clear pending requests for a specific pattern
 */
export function clearPendingRequests(pattern?: string) {
  if (pattern) {
    // Clear entries matching pattern
    for (const key of pendingRequests.keys()) {
      if (key.includes(pattern)) {
        pendingRequests.delete(key);
      }
    }
  } else {
    // Clear all pending requests
    pendingRequests.clear();
  }
}
